import { makeStyles, tokens } from '@fluentui/react-components';

export const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    minWidth: '0',
    overflow: 'hidden',
  },

  scroll: {
    flex: '1',
    overflowY: 'auto',
    minHeight: '0',
    // Pre-existing styling — when this page rendered as a top-level route the
    // App layout's content padding gave the content horizontal breathing room.
    // Now that we render nested in EvaluationTemplatesPage's center pane,
    // there's no outer padding, so add it here.
    paddingTop: tokens.spacingVerticalL,
    paddingRight: tokens.spacingHorizontalXL,
    paddingBottom: tokens.spacingVerticalXXL,
    paddingLeft: tokens.spacingHorizontalXL,
  },

  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    paddingBottom: tokens.spacingVerticalM,
  },

  breadcrumbLink: {
    color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
      color: tokens.colorNeutralForeground1,
    },
  },

  breadcrumbCurrent: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },

  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalL,
  },

  subtitle: {
    color: tokens.colorNeutralForeground3,
  },

  cards: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },

  // Query set + Data set sit side-by-side when the form column is wide
  // enough, and stack to full width when it's narrow (small screens / the
  // agent Evaluate tab, where the Jobs rail eats horizontal space). auto-fit
  // + minmax responds to the column's own width, so no viewport breakpoint is
  // needed; `min(100%, …)` keeps a single column from overflowing when the
  // column is narrower than the track's minimum.
  twoColumn: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 22rem), 1fr))',
    gap: tokens.spacingHorizontalL,
  },

  card: {
    paddingTop: tokens.spacingVerticalL,
    paddingRight: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: tokens.borderRadiusMedium,
  },

  cardLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    paddingBottom: tokens.spacingVerticalS,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },

  cardLabelHelp: {
    textTransform: 'none',
    letterSpacing: 'normal',
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
  },

  sourceCardHeader: {
    height: '32px',
    minHeight: '32px',
    boxSizing: 'border-box',
    flexShrink: 0,
  },

  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },

  segmentTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },

  segmentDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  segmentNote: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },

  // Two-line content for a Dropdown <Option> — title over a muted
  // description, mirroring the old SegmentCard layout inside the listbox.
  optionStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },

  // Compact inline Field (horizontal orientation) — label sits beside the
  // control instead of stacked above it, width-capped so single-select
  // dropdowns stay tight rather than spanning the full card.
  inlineField: {
    maxWidth: '360px',
  },

  // A bare compact Dropdown (no Field wrapper) — used where the card title
  // already labels the control.
  compactSelect: {
    maxWidth: '280px',
  },

  // A selected grader, shown as a compact row with a remove button —
  // mirrors the Variables list layout.
  graderChipRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalXS,
    paddingRight: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },

  graderChipName: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    minWidth: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  twoColumnFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
    // Align cells to the top so a Field with a `hint` underneath doesn't
    // stretch the sibling Field's input downward to the row's full height.
    alignItems: 'start',
  },

  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },

  helperText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },

  noteCallout: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    paddingTop: tokens.spacingVerticalXS,
    paddingRight: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },

  fileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },

  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },

  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
  },

  // Strips the default <fieldset> chrome (border, padding, margin) and
  // makes it `display: contents` so descendants flow into the parent grid
  // as if the fieldset wasn't there. We still need the element itself so
  // `disabled` cascades to nested form controls in view mode.
  fieldsetReset: {
    display: 'contents',
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    paddingTop: '0',
    paddingRight: '0',
    paddingBottom: '0',
    paddingLeft: '0',
    marginTop: '0',
    marginRight: '0',
    marginBottom: '0',
    marginLeft: '0',
    // View mode disables the whole fieldset, but Fluent v9 controls don't
    // pick up the native `:disabled` state visually — so read-only fields
    // still look editable. Mute the control surfaces here so it's obvious
    // they're not editable. Only applies when the fieldset is disabled.
    '&:disabled .fui-Input': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      cursor: 'not-allowed',
    },
    '&:disabled .fui-Textarea': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      cursor: 'not-allowed',
    },
    '&:disabled .fui-Dropdown': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      cursor: 'not-allowed',
    },
    '&:disabled .fui-Combobox': {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      cursor: 'not-allowed',
    },
    '&:disabled .fui-Input > input': {
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
    },
    '&:disabled .fui-Textarea > textarea': {
      color: tokens.colorNeutralForegroundDisabled,
      cursor: 'not-allowed',
    },
  },
});
